import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import PWAInstallPrompt from "./components/ui/PWAInstallPrompt";
import UpdateNotification from "./components/ui/UpdateNotification";

// Pages
import LoginPage from "./components/pages/LoginPage";
import GuruDashboard from "./components/pages/GuruDashboard";
import KepsekDashboard from "./components/pages/KepsekDashboard";
import AdminDashboard from "./components/pages/AdminDashboard";
import AbsensiPage from "./components/pages/AbsensiPage";
import RiwayatPage from "./components/pages/RiwayatPage";
import IzinPage from "./components/pages/IzinPage";
import LaporanPage from "./components/pages/LaporanPage";
import PetaPage from "./components/pages/PetaPage";
import SekolahPage from "./components/pages/SekolahPage";
import AuditPage from "./components/pages/AuditPage";
import FotoKegiatanPage from "./components/pages/FotoKegiatanPage";
import ProfilPage from "./components/pages/ProfilPage";
import UserManagementPage from "./components/pages/UserManagementPage";
import HariAktifPage from "./components/pages/HariAktifPage";
import FaceRegistrationPage from "./components/pages/FaceRegistrationPage";
import KioskManagementPage from "./components/pages/KioskManagementPage";
import KioskPage from "./components/pages/KioskPage";
import PengaturanPage from "./components/pages/PengaturanPage";
import SecurityAlertsPage from "./components/pages/SecurityAlertsPage";
import LivenessReportPage from "./components/pages/LivenessReportPage";
import PanduanPage from "./components/pages/PanduanPage";
import SuratTugasPage from "./components/pages/SuratTugasPage";
import VersionManagementPage from "./components/pages/VersionManagementPage";
import BulkImportPage from "./components/pages/BulkImportPage";
import InstansiPage from "./components/pages/InstansiPage";
import LaporanInstansiPage from "./components/pages/LaporanInstansiPage";
import PegawaiInstansiDashboard from "./components/pages/PegawaiInstansiDashboard";
import DownloadPage from "./components/pages/DownloadPage";
import KebijakanPrivasiPage from "./components/pages/KebijakanPrivasiPage";
import DashboardLayout from "./components/layout/DashboardLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fcfbf9]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-950 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Memuat...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router - redirects to appropriate dashboard based on role
const DashboardRouter = () => {
  const { user } = useAuth();

  if (user?.role === "guru" || user?.role === "tenaga_tata_usaha") {
    return <GuruDashboard />;
  } else if (user?.role === "kepala_sekolah") {
    return <KepsekDashboard />;
  } else if (["admin_dinas", "pimpinan", "editor", "pengawas"].includes(user?.role)) {
    return <AdminDashboard />;
  } else if (user?.role === "pegawai_instansi") {
    return <PegawaiInstansiDashboard />;
  }

  return <Navigate to="/login" replace />;
};

// Register Service Worker
const registerServiceWorker = async () => {
  if ("serviceWorker" in navigator) {
    try {
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });
      console.log("SW registered:", registration.scope);

      // Listen for SW messages
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data.type === "SYNC_ATTENDANCE") {
          console.log("Sync attendance triggered from SW");
          window.dispatchEvent(new CustomEvent("sync-attendance"));
        }
      });
    } catch (error) {
      console.log("SW registration failed:", error);
    }
  }
};

function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Kiosk Route (Public - no auth required) */}
            <Route path="/kiosk/:deviceToken" element={<KioskPage />} />

            {/* Download APK Page (Public - no auth required) */}
            <Route path="/download" element={<DownloadPage />} />

            {/* Privacy Policy (Public - required by Play Store) */}
            <Route path="/kebijakan-privasi" element={<KebijakanPrivasiPage />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardRouter />} />

              {/* Guru Routes */}
              <Route
                path="absensi"
                element={
                  <ProtectedRoute allowedRoles={["guru", "kepala_sekolah", "pengawas", "tenaga_tata_usaha", "pegawai_instansi", "pimpinan", "editor"]}>
                    <AbsensiPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="riwayat"
                element={
                  <ProtectedRoute allowedRoles={["guru", "kepala_sekolah", "admin_dinas", "pimpinan", "pengawas", "editor", "tenaga_tata_usaha", "pegawai_instansi"]}>
                    <RiwayatPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="foto-kegiatan"
                element={
                  <ProtectedRoute allowedRoles={["guru", "kepala_sekolah"]}>
                    <FotoKegiatanPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="profil"
                element={
                  <ProtectedRoute allowedRoles={["guru", "kepala_sekolah", "admin_dinas", "pimpinan", "editor", "pengawas", "tenaga_tata_usaha", "pegawai_instansi"]}>
                    <ProfilPage />
                  </ProtectedRoute>
                }
              />

              {/* Shared Routes */}
              <Route
                path="izin"
                element={
                  <ProtectedRoute
                    allowedRoles={["guru", "kepala_sekolah", "admin_dinas", "pimpinan", "pengawas", "editor", "tenaga_tata_usaha", "pegawai_instansi"]}
                  >
                    <IzinPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="izin-sekolah"
                element={
                  <ProtectedRoute
                    allowedRoles={["kepala_sekolah", "admin_dinas", "pimpinan", "editor", "pengawas"]}
                  >
                    <IzinPage orgType="sekolah" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="izin-instansi"
                element={
                  <ProtectedRoute
                    allowedRoles={["admin_dinas", "pimpinan", "editor"]}
                  >
                    <IzinPage orgType="instansi" />
                  </ProtectedRoute>
                }
              />

              {/* Kepala Sekolah & Admin Routes */}
              <Route
                path="laporan"
                element={
                  <ProtectedRoute allowedRoles={["kepala_sekolah", "admin_dinas", "pimpinan"]}>
                    <LaporanPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="peta"
                element={
                  <ProtectedRoute allowedRoles={["kepala_sekolah", "admin_dinas", "pimpinan"]}>
                    <PetaPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="guru"
                element={
                  <ProtectedRoute allowedRoles={["kepala_sekolah", "admin_dinas", "pimpinan", "editor", "pengawas"]}>
                    <RiwayatPage orgType="sekolah" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="riwayat-guru"
                element={
                  <ProtectedRoute allowedRoles={["kepala_sekolah", "admin_dinas", "pimpinan", "editor", "pengawas"]}>
                    <RiwayatPage orgType="sekolah" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="riwayat-instansi"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "pimpinan"]}>
                    <RiwayatPage orgType="instansi" />
                  </ProtectedRoute>
                }
              />

              {/* Admin Only Routes */}
              <Route
                path="sekolah"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas"]}>
                    <SekolahPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="instansi"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas"]}>
                    <InstansiPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="laporan-instansi"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "pimpinan"]}>
                    <LaporanInstansiPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="bulk-import"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas"]}>
                    <BulkImportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="audit"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "pimpinan"]}>
                    <AuditPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "pimpinan", "editor"]}>
                    <UserManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="hari-aktif"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "pimpinan"]}>
                    <HariAktifPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="face-registration"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "kepala_sekolah"]}>
                    <FaceRegistrationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="kiosk-management"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "kepala_sekolah"]}>
                    <KioskManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="pengaturan"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas"]}>
                    <PengaturanPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="security-alerts"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "kepala_sekolah", "pimpinan", "editor"]}>
                    <SecurityAlertsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="liveness-report"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "kepala_sekolah", "pimpinan"]}>
                    <LivenessReportPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="surat-tugas"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas", "kepala_sekolah", "guru", "pimpinan", "pengawas", "editor", "tenaga_tata_usaha", "pegawai_instansi"]}>
                    <SuratTugasPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="version-management"
                element={
                  <ProtectedRoute allowedRoles={["admin_dinas"]}>
                    <VersionManagementPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="panduan"
                element={
                  <ProtectedRoute allowedRoles={["guru", "kepala_sekolah", "admin_dinas", "pimpinan", "editor", "pengawas"]}>
                    <PanduanPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
          
          {/* PWA Update Notification */}
          <UpdateNotification />
        </div>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
